import { useEffect, useState } from "react";

/**
 * Reusable countdown / count-up banner in the sci-fi hero style.
 *
 * Props:
 *  - target: Date            required — reference point
 *  - direction: "down" | "up"   default "down". "down" = counts down TO target (freezes at 0 once passed).
 *                                        "up"   = counts up FROM target (freezes at 0 until reached).
 *  - eyebrow: string         label above the date
 *  - dateLabel: string       small label under the eyebrow (shown before reaching target)
 *  - liveLabel: string       small label shown once counter is past its target
 *                              ("Live · jump in now" for down mode, "Live since …" for up mode)
 *  - accent: "cyan" | "green"   default "cyan". "green" is used for live/pioneer to differentiate from open-beta.
 *  - labels: {days, hours, minutes, seconds}   short unit captions
 *  - testId: string          data-testid for the outer wrapper (default "countdown-banner")
 */
const ACCENT_MAP = {
  cyan: {
    dot:       "bg-[var(--t-blue)]",
    text:      "text-[var(--t-blue-glow)]",
    number:    "text-[var(--t-cyan)]",
    numberGlow:"0 0 12px rgba(102, 230, 255, 0.55)",
    scan:      "via-[var(--t-cyan)]",
  },
  green: {
    dot:       "bg-[#5fd99a]",
    text:      "text-[#5fd99a]",
    number:    "text-[#5fd99a]",
    numberGlow:"0 0 12px rgba(95, 217, 154, 0.55)",
    scan:      "via-[#5fd99a]",
  },
};

function compute(target, direction) {
  const now = Date.now();
  const targetMs = target.getTime();
  let ms;
  if (direction === "up") {
    ms = now - targetMs;
  } else {
    ms = targetMs - now;
  }
  const past = ms <= 0;
  if (past) ms = 0;
  const days    = Math.floor(ms / 86_400_000);
  const hours   = Math.floor((ms % 86_400_000) / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  const seconds = Math.floor((ms % 60_000) / 1000);
  return { days, hours, minutes, seconds, past };
}

export default function CountdownBanner({
  target,
  direction = "down",
  eyebrow,
  dateLabel,
  liveLabel,
  accent = "cyan",
  labels,
  testId = "countdown-banner",
}) {
  const [time, setTime] = useState(() => compute(target, direction));

  useEffect(() => {
    const id = setInterval(() => setTime(compute(target, direction)), 1000);
    return () => clearInterval(id);
  }, [target, direction]);

  const a = ACCENT_MAP[accent] || ACCENT_MAP.cyan;
  // "Live" state:
  //   - down mode: after the target passed (past === true)
  //   - up mode:   as long as the target is in the past (past === false in our compute; up-mode passed==false means diff>0 → we ARE live)
  const isLive = direction === "up" ? !time.past : time.past;
  const smallLabel = isLive ? liveLabel : dateLabel;

  return (
    <div
      className="relative panel corner-frame p-4 md:p-5 mb-6 md:mb-8 overflow-hidden"
      data-testid={testId}
    >
      <span
        aria-hidden
        className={`absolute -inset-x-2 top-0 h-px bg-gradient-to-r from-transparent ${a.scan} to-transparent opacity-60`}
      />
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`inline-block w-2 h-2 rounded-full ${a.dot} pulse-glow flex-shrink-0`} aria-hidden />
            <span className={`font-mono text-[10px] md:text-[11px] tracking-[0.25em] md:tracking-[0.3em] ${a.text} uppercase`}>
              {eyebrow}
            </span>
          </div>
          <div className="font-mono text-[10px] md:text-xs text-[var(--t-text-muted)] tracking-[0.18em] uppercase mt-1">
            {smallLabel}
          </div>
        </div>
        <div className="flex items-stretch gap-2 md:gap-3" data-testid={`${testId}-units`}>
          <Unit value={time.days}    label={labels.days}    testid="days" accent={a} />
          <Sep accent={a} />
          <Unit value={time.hours}   label={labels.hours}   testid="hrs" accent={a} />
          <Sep accent={a} />
          <Unit value={time.minutes} label={labels.minutes} testid="min" accent={a} />
          <Sep accent={a} />
          <Unit value={time.seconds} label={labels.seconds} testid="sec" accent={a} pulse />
        </div>
      </div>
    </div>
  );
}

function Unit({ value, label, testid, pulse = false, accent }) {
  const display = String(value).padStart(2, "0");
  return (
    <div
      className="flex flex-col items-center justify-center min-w-[54px] md:min-w-[68px] px-1.5 py-1.5 md:py-2 border border-[var(--t-border-glow)] bg-[#050912]"
      data-testid={`countdown-${testid}`}
    >
      <span
        className={`font-display text-xl md:text-3xl leading-none ${accent.number} tabular-nums ${pulse ? "pulse-glow" : ""}`}
        style={{ textShadow: accent.numberGlow }}
      >
        {display}
      </span>
      <span className="mt-1 font-mono text-[9px] md:text-[10px] tracking-[0.22em] text-[var(--t-text-muted)] uppercase">
        {label}
      </span>
    </div>
  );
}

function Sep({ accent }) {
  return (
    <span
      aria-hidden
      className={`self-center font-display text-lg md:text-2xl ${accent.text} opacity-60 leading-none`}
    >
      :
    </span>
  );
}
